import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import {} from 'react';
import { useAuth } from './AuthProvider';
import { getMessages } from '../i18n';
/**
 * Session gate for shells with a real (interactive) auth provider: children
 * render only once authenticated. Mock auth passes through instantly (its
 * status is always 'authenticated'), so wrapping unconditionally is safe.
 * The OIDC callback route must mount OUTSIDE this gate — the code exchange
 * runs unauthenticated by definition.
 */
export function AuthGate({ children, locale }) {
    const auth = useAuth();
    // ⛔ Resolved PER RENDER from an optional prop, not once at module scope.
    //
    // This is shared copy rendered by surfaces with different locale sources. A module-scope
    // `const t = getMessages()` baked in `detectLocale()` at import time, so the fixed-RU admin
    // panels — which correctly declare `<html lang="ru">` via UI_LOCALE — rendered ENGLISH
    // sign-in/loading/session-ended copy under it for any `en-*` browser. Found by product on
    // `!509` (2026-08-31); the panels were never detection-driven, so nothing downstream could
    // correct it.
    //
    // Omitting the prop keeps the old behaviour exactly (the default is still `detectLocale()`),
    // which is what the detection-driven surfaces want. A surface that renders a fixed dictionary
    // passes ITS locale, so one decision drives the copy and `<html lang>` together.
    const t = getMessages(locale);
    if (auth.status === 'loading') {
        return (_jsx("div", { className: "flex h-full items-center justify-center p-6 text-center", children: _jsx("p", { className: "text-muted", children: t.auth.loading }) }));
    }
    if (auth.status === 'unauthenticated') {
        // `sessionEnded` distinguishes a session dropped out from under the user
        // (bridge cleared its store on a refresh rejection, web!84) from a plain
        // not-signed-in state — the former needs to explain WHY they're back at the
        // gate, not read as a silent broken/disconnected state.
        return (_jsxs("div", { className: "flex h-full flex-col items-center justify-center gap-4 p-6 text-center", children: [auth.loginFailure !== undefined && (
                // A failed attempt must not read as "never tried" (desktop, 2026-09-23).
                // First failure: generic copy, the class as an attribute only. A repeat
                // stops promising a retry and names the class as a code for support.
                _jsx("p", { role: "alert", "data-testid": "login-failed", "data-login-failure": auth.loginFailure, "data-login-failure-elapsed-ms": auth.loginFailureElapsedMs, className: "font-medium", children: (auth.loginFailures ?? 0) >= 2
                        ? t.auth.signInFailedRepeat.replace('{code}', () => auth.loginFailure ?? '')
                        : t.auth.signInFailed })), _jsx("p", { className: "text-muted", children: auth.sessionEnded ? t.auth.sessionEndedPrompt : t.auth.signInPrompt }), _jsx("button", { type: "button", onClick: auth.login, className: "rounded-md bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent-deep", children: t.auth.signIn })] }));
    }
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=AuthGate.js.map